<template>
  <div class="relative flex h-full flex-col space-y-5" data-cy="offers-stage">
    <div class="space-y-2">
      <div class="relative flex justify-between space-x-2">
        <div class="flex w-full items-center justify-between">
          <lf-h2 class="mb-0">
            {{ $t("OFFER_CUSTOMISATION.SEND_AN_OFFER") }}
          </lf-h2>
          <div>
            <search-input
              v-model="search"
              class="w-64"
              :placeholder="`${$t('COMMON.SEARCH')} ${$t('COMMON.PRODUCT')}`"
              no-margin
            />
          </div>
        </div>
        <div class="flex space-x-2">
          <offer-delete-button
            v-if="selectedOffers.length && canCreateOffer"
            :offers="selectedOffers"
            class="fixed top-0 right-0"
            @offer-deleted="selectedOffers = []"
          />
          <create-self-funded-offer-button
            v-if="shouldShowSelfFundedOfferButton && canCreateOffer"
            :disabled="(isClient && !isUnderwritingEnabled) || isClientUser"
            @click="clearSelectedOffers"
          />
        </div>
      </div>
      <div>
        {{ $t("OFFER_CUSTOMISATION.SEND_AN_OFFER_DESCRIPTION") }}
      </div>
    </div>
    <template v-if="groupedOffers.length || search.length">
      <draggable
        :model-value="groupedOffers"
        item-key="id"
        @update:model-value="updateWeight"
      >
        <template #item="{ element: funder }">
          <funder-card
            multi-select
            is-row-clickable
            show-bulk-select-header
            :grouped-offers="funder"
            :is-closing="false"
            :sole-funder="groupedOffers.length === 1 && !hasPlacementStage"
            :selected-offers="selectedOffers"
            @update-selected-offers="handleOfferSelection"
          />
        </template>
      </draggable>
    </template>
    <template v-else>
      <offers-empty :is-self-funding="isClientFunded" />
    </template>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import draggable from "vuedraggable";
import { useStore } from "vuex";

import compact from "lodash/compact";

import { Ability, PermissionSubject } from "@/enums/auth";
import { offersGroupedByFunder } from "@/helpers/offers";
import { useAuth } from "@/hooks/auth";
import { usePermissions } from "@/hooks/auth";
import { useDeals } from "@/hooks/deals";
import { useActiveWorkflowTemplate } from "@/hooks/workflow";

import FunderCard from "@/components/deals/workflow/OfferStage/WorkflowLive/FunderCard.vue";
import OfferDeleteButton from "@/components/ui/buttons/OfferDeleteButton.vue";
import CreateSelfFundedOfferButton from "@/views/deals/components/CreateSelfFundedOfferButton.vue";
import OffersEmpty from "@/views/deals/components/OffersEmpty.vue";

import type { IOffer } from "@/models/funders";
import type { GroupedOffer } from "@/models/offers";

const { getters, dispatch } = useStore();
const { canPerformActionReactive } = usePermissions();

const {
  isClientFunded,
  isUnderwritingEnabled,
  isActiveDealSelfFunded,
  activeDeal,
} = useDeals();

const canCreateOffer = canPerformActionReactive(
  PermissionSubject.offer,
  Ability.create,
  { application: activeDeal.value?.id }
);

const { isLendflowUser, isClient, isClientUser } = useAuth();
const selectedOffers = ref<IOffer[]>([]);
const search = ref("");

const { hasStageTypeComputed, allSelectedOfferFunders } =
  useActiveWorkflowTemplate();

const offers = computed<IOffer[]>(() => getters["applications/offers"]);

const groupedOffers = computed(() => {
  return offersGroupedByFunder(offers.value, {
    origin_template_name: search.value,
    offerFunders: allSelectedOfferFunders.value,
  });
});
const hasPlacementStage = hasStageTypeComputed("placement");

const shouldShowSelfFundedOfferButton = computed(() => {
  return (
    ((isActiveDealSelfFunded.value && !hasPlacementStage.value) ||
      (!hasPlacementStage.value && isLendflowUser)) &&
    groupedOffers.value.length
  );
});

const handleOfferSelection = (offers: IOffer[], selected: boolean | string) => {
  const offerIds = offers.map(({ id }) => id);
  const excludedOffers = selectedOffers.value.filter(
    (offerCurrent) => !offerIds.includes(offerCurrent.id)
  );

  selectedOffers.value = selected
    ? [...excludedOffers, ...offers]
    : excludedOffers;
};

if (isActiveDealSelfFunded.value) {
  dispatch("applications/getPlacements");
}

const clearSelectedOffers = () => {
  selectedOffers.value = [];
};

const updateWeight = (offers: GroupedOffer[]) => {
  if (!offers.length) {
    return;
  }

  const changedOrderRequest = compact(
    offers.map((offer, index) => {
      if (index === offer.weight) {
        return;
      }

      return {
        id: offer.id,
        offer_template_id: offer.product_id,
        offer_template_weight: index,
      };
    })
  );

  if (!changedOrderRequest.length) {
    return;
  }

  dispatch("workflows/changeOfferTemplatesWeight", changedOrderRequest);
};
</script>
